import { useState, useMemo } from 'react'
import { Container, Typography, Box, Paper } from '@mui/material'
import { Calendar, dateFnsLocalizer, View } from 'react-big-calendar'
import { format, parse, startOfWeek, getDay } from 'date-fns'
import { enUS } from 'date-fns/locale'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { useTaskStore } from '@/store/useTaskStore'
import { useFamilyStore } from '@/store/useFamilyStore'
import { useTaskOccurrences } from '@/hooks/useTaskOccurrences'
import TaskForm from '@/components/tasks/TaskForm'
import TaskCompletionDialog from '@/components/tasks/TaskCompletionDialog'
import type { Task, CalendarEvent } from '@/types'

const locales = {
  'en-US': enUS,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
})

export default function CalendarPage() {
  const [date, setDate] = useState(new Date())
  const [view, setView] = useState<View>('month')
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [completingTask, setCompletingTask] = useState<Task | null>(null)

  const tasks = useTaskStore((state) => state.tasks)
  const members = useFamilyStore((state) => state.members)
  const occurrences = useTaskOccurrences(tasks, date, view === 'month' ? 'month' : view === 'week' ? 'week' : 'day')

  // Convert task occurrences to calendar events
  const events: CalendarEvent[] = useMemo(() => {
    return occurrences.map(({ task, date: occurrenceDate }) => ({
      id: `${task.id}-${occurrenceDate.getTime()}`,
      title: task.title,
      start: occurrenceDate,
      end: new Date(occurrenceDate.getTime() + task.estimated_duration * 60000),
      resource: task,
    }))
  }, [occurrences])

  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedTask(event.resource)
    setFormOpen(true)
  }

  const handleSelectSlot = () => {
    setSelectedTask(null)
    setFormOpen(true)
  }

  const eventStyleGetter = (event: CalendarEvent) => {
    const member = members.find((m) => m.id === event.resource.assigned_to)
    const backgroundColor = member?.color || '#1976d2'

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    }
  }

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Calendar
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View all scheduled cleaning tasks
        </Typography>
      </Box>

      <Paper sx={{ p: 2, height: 'calc(100vh - 250px)', minHeight: 600 }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          view={view}
          onView={(newView) => setView(newView)}
          date={date}
          onNavigate={(newDate) => setDate(newDate)}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          selectable
          eventPropGetter={eventStyleGetter}
          popup
          style={{ height: '100%' }}
        />
      </Paper>

      {/* Task Form Dialog */}
      <TaskForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false)
          setSelectedTask(null)
        }}
        task={selectedTask}
      />

      {/* Task Completion Dialog */}
      <TaskCompletionDialog
        open={Boolean(completingTask)}
        onClose={() => setCompletingTask(null)}
        task={completingTask}
      />
    </Container>
  )
}
